import React, { useCallback, useMemo, useState } from 'react';
import { Pressable, Share, StyleSheet, View } from 'react-native';
import FastImage from '@d11/react-native-fast-image';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { mvs } from 'react-native-size-matters';

import { EpisodeHeader } from './EpisodeHeader';
import { EpisodeHeaderNav } from './EpisodeHeaderNav';
import { HTMLContentRenderer } from './HTMLContentRenderer';
import { PlaybackSpeed } from './PlaybackSpeed';
import { PlayerControls } from './PlayerControls';
import { PlayerProgress } from './PlayerProgress';
import { PodcastPlayerMenu } from './PodcastPlayerMenu';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { COLORS, IS_ANDROID, IS_IOS, isTablet } from '../../constants';
import {
  selectAppConfig,
  selectDefaultCover,
  selectDivRadius,
} from '../../features/appconfig/store/selectors';
import { selectCurrentEpisode } from '../../features/currentpodcast/store/selectors';
import { hidePlayerModal } from '../../features/currentpodcast/store/slice';
import useOrientation from '../../hooks/useOrientation';
import { usePodcastNavigation } from '../../hooks/usePodcastNavigation';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import i18n from '../../localization/i18n';
import { CustomIcon } from '../CustomIcon';
import CustomText from '../CustomText';
import { ScreenLayout } from '../ScreenLayout';
import { SleepTimer } from '../SleepTimer';

interface PodcastPlayerProps {
  onBackPress?: () => void;
}

export const PodcastPlayer: React.FC<PodcastPlayerProps> = ({ onBackPress }) => {
  const dispatch = useAppDispatch();
  const currentEpisode = useAppSelector(selectCurrentEpisode);
  const { navigateToPodcastFeed, navigateToPodcastEpisode } = usePodcastNavigation();
  const [menuVisible, setMenuVisible] = useState(false);
  const { bodyHorizontalPadding, maxWidth } = useResponsiveLayout();
  const defaultCover = useAppSelector(selectDefaultCover);
  const divRadius = useAppSelector(selectDivRadius);
  const appConfig = useAppSelector(selectAppConfig);

  const handleMenuPress = () => {
    setMenuVisible(true);
  };

  const handleMenuClose = () => {
    setMenuVisible(false);
  };

  const handlePodcastPress = useCallback(() => {
    if (currentEpisode?.podcastUrl) {
      dispatch(hidePlayerModal());
      navigateToPodcastFeed(currentEpisode.podcastUrl);
    }
  }, [currentEpisode.podcastUrl, dispatch, navigateToPodcastFeed]);

  const handleEpisodePress = useCallback(() => {
    if (currentEpisode) {
      dispatch(hidePlayerModal());
      navigateToPodcastEpisode(currentEpisode);
    }
  }, [currentEpisode, dispatch, navigateToPodcastEpisode]);

  const insets = useSafeAreaInsets();

  // tablet specific styles
  const orientation = useOrientation();
  const isTabletLandscape = isTablet && orientation === 'LANDSCAPE';
  const isTabletPortrait = isTablet && orientation === 'PORTRAIT';

  const shareIcon = useMemo(() => {
    const message = currentEpisode.title + ' (' + currentEpisode.podcastTitle + ')';
    return (
      <CustomIcon
        name="share_player"
        color={COLORS.WHITE}
        size={mvs(24)}
        containerStyle={styles.shareIcon}
        onPress={() => {
          Share.share({
            message:
              i18n.t('share.podcast', {
                podcast: message,
                appName: appConfig.nomRadio,
              }) + (IS_ANDROID ? '\n' + currentEpisode.url : ''),
            url: currentEpisode.url,
          });
        }}
      />
    );
  }, [appConfig, currentEpisode]);

  return (
    <View style={[styles.container]}>
      <ScreenLayout
        noMiniPlayer
        isPodcastModule
        headerStyle={IS_IOS && { marginTop: insets.top }}
        headerContents={
          <EpisodeHeaderNav
            podcastThumbnail={currentEpisode.podcastLogo}
            podcastName={currentEpisode.podcastTitle}
            onBackPress={onBackPress || (() => {})}
            onMenuPress={handleMenuPress}
            onPodcastPress={handlePodcastPress}
          />
        }
      >
        {/* episode content */}
        <View
          style={[
            styles.content,
            {
              paddingHorizontal: bodyHorizontalPadding,
              maxWidth,
            },
          ]}
        >
          <View style={styles.episodeSection}>
            {isTabletPortrait && (
              <Pressable style={styles.podcastInfoRow} onPress={handlePodcastPress}>
                <FastImage
                  source={
                    currentEpisode.podcastLogo
                      ? {
                          uri: currentEpisode.podcastLogo,
                        }
                      : defaultCover
                  }
                  style={[styles.podcastThumbnail, { borderRadius: mvs(divRadius) }]}
                  resizeMode={FastImage.resizeMode.cover}
                />
                <CustomText
                  style={[styles.podcastName]}
                  weight="SemiBold"
                  numberOfLines={1}
                >
                  {currentEpisode.podcastTitle}
                </CustomText>
              </Pressable>
            )}
            {/* episode header with image and title */}
            <EpisodeHeader
              episodeImage={currentEpisode.image}
              episodeTitle={currentEpisode.title}
              episodeDate={currentEpisode.date}
            />
            {/* episode description */}
            {!isTabletLandscape && (
              <View style={styles.description}>
                <HTMLContentRenderer
                  content={currentEpisode.content}
                  opacity={0.7}
                  maxCharacters={isTablet ? 200 : 100}
                  textColor={COLORS.WHITE}
                  textAlign={'center'}
                />
              </View>
            )}
          </View>

          {/* player controls */}
          <View style={styles.progressSection}>
            <PlayerProgress />
          </View>

          <View>
            <PlayerControls />
          </View>

          {/* bottom actions */}
          <View style={styles.bottomActions}>
            <PlaybackSpeed />
            {shareIcon}
            <SleepTimer />
          </View>
          <SafeAreaView edges={['bottom']} />
        </View>
      </ScreenLayout>

      {/* menu (hidden) */}
      <PodcastPlayerMenu
        handleViewPodcastPage={handlePodcastPress}
        visible={menuVisible}
        onRequestClose={handleMenuClose}
        handleViewEpisodePage={handleEpisodePress}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    maxWidth: '100%',
  },
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  episodeSection: {
    alignItems: 'center',
    gap: mvs(16),
  },
  podcastInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: '60%',
    gap: mvs(12),
  },
  podcastThumbnail: {
    width: mvs(40),
    height: mvs(40),
  },
  podcastName: {
    color: COLORS.WHITE,
  },
  description: {
    width: '100%',
  },
  progressSection: {
    width: '100%',
  },
  bottomActions: {
    opacity: 0.7,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  shareIcon: {
    marginRight: mvs(19),
    marginLeft: mvs(11),
  },
});

export default PodcastPlayer;
